<template>
  <v-btn v-on="$listeners" v-bind="vBtnProps" class="ui-btn-width" :class="btnClass">
    <span v-if="$scopedSlots.iconLeft" class="btn-icon mr-2" :class="textClass">
      <slot name="iconLeft" />
    </span>
    <span :class="textClass">
      <slot />
    </span>
    <span v-if="$scopedSlots.iconRight" class="btn-icon ml-2" :class="textClass">
      <slot name="iconRight" />
    </span>
  </v-btn>
</template>
<script>
export default {
  name: 'UiButton',
  props: {
    variant: {
      type: String,
      default: 'primary',
      // primary: dark-blue fill / secondary: white with border / apply: blue fill / error: red fill
      validator: v => ['primary', 'secondary', 'apply', 'error'].includes(v),
    },
    size: {
      type: String,
      default: 'default',
      validator: v => ['default', 'x-small', 'small', 'large', 'x-large'].includes(v),
    },
    isIcon: { type: Boolean, default: false },
    round: { type: Boolean, default: false },
    block: { type: Boolean, default: false },
    w50: { type: Boolean, default: false },
    w120: { type: Boolean, default: false },
    w120sm100: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    type: { type: String, default: 'button' },
  },
  computed: {
    vBtnProps() {
      const sizeProps = this.size !== 'default' ? { [this.size]: true } : {}
      return {
        ...this.variantProps,
        ...sizeProps,
        block: this.block,
        rounded: this.round,
        disabled: this.disabled,
        loading: this.loading,
        type: this.type,
        elevation: 0,
      }
    },
    variantProps() {
      if (this.isIcon && this.variant === 'secondary') {
        return {
          outlined: true,
          tile: !this.round,
          color: 'black lighten-6',
        }
      }
      if (this.isIcon) {
        return {
          text: true,
          icon: true,
          tile: !this.round,
          color: 'black lighten-3',
        }
      }
      switch (this.variant) {
        case 'primary':
          return { color: 'blue500' }
        case 'secondary':
          return { color: 'black lighten-6', outlined: true }
        case 'apply':
          return {}
        case 'error':
          return { color: 'red500' }
        default:
          return { color: 'blue500' }
      }
    },
    btnClass() {
      return {
        'variant-apply': this.variant === 'apply' && !this.isIcon,
        'width-120': this.w120,
        'width-50': this.w50,
        'width-120-sm-100': this.w120sm100,
        'icon-box': this.isIcon && this.variant === 'secondary',
      }
    },
    textClass() {
      if (this.isIcon) return {}
      switch (this.variant) {
        case 'primary':
        case 'error':
          return { 'white--text': true }
        case 'secondary':
          return { 'gray800--text': !this.disabled }
        default:
          return {}
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.ui-btn-width.variant-apply {
  min-width: 120px !important;
  background-color: var(--v-blue50-base) !important;
  & span {
    color: var(--v-blue500-base) !important;
  }
  @media #{map-get($display-breakpoints, 'xs-only')} {
    width: 48% !important;
  }
}
.width-120 {
  min-width: 120px !important;
  @media #{map-get($display-breakpoints, 'xs-only')} {
    width: 47% !important;
  }
}
.width-50 {
  width: 49% !important;
}
.width-120-sm-100 {
  min-width: 120px !important;
  @media #{map-get($display-breakpoints, 'xs-only')} {
    width: 100% !important;
  }
}
.icon-box {
  width: 40px !important;
  height: 40px !important;
  min-width: 40px !important;
  padding: 0 !important;
}
::v-deep {
  .btn-icon .v-icon {
    color: currentColor !important;
  }
  &.v-btn.v-btn--outlined.theme--light:not(.v-btn--disabled) {
    background-color: white;
  }
  &.v-btn.v-btn--disabled.v-btn--outlined.theme--light {
    background-color: rgba(0, 0, 0, 0.12) !important;
    border: none !important;
    opacity: 1 !important;
    .v-btn__content {
      color: #525252 !important;
      .v-icon {
        color: #525252 !important;
      }
    }
    &::before {
      display: none !important;
    }
  }
  &.v-btn.v-btn--disabled.variant-apply.theme--light {
    .v-btn__content span {
      color: var(--v-gray400-base) !important;
    }
  }
}
</style>
